import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from './Button'

export interface PaginationProps {
  page: number
  perPage: number
  total: number
  onPageChange: (page: number) => void
}

/** Pages visibles autour de la page courante, avec ellipses. */
export function pageWindow(page: number, last: number): (number | 'gap')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)
  const pages = new Set([1, last, page - 1, page, page + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b)
  const result: (number | 'gap')[] = []
  sorted.forEach((p, index) => {
    if (index > 0 && p - sorted[index - 1]! > 1) result.push('gap')
    result.push(p)
  })
  return result
}

export function Pagination({ page, perPage, total, onPageChange }: PaginationProps) {
  const last = Math.max(1, Math.ceil(total / perPage))
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(total, page * perPage)
  const fmt = new Intl.NumberFormat('fr-FR')

  return (
    <nav className="pagination" aria-label="Pagination">
      <span>
        {fmt.format(from)}–{fmt.format(to)} sur {fmt.format(total)}
      </span>
      <div className="pagination-pages">
        <Button
          size="sm"
          variant="ghost"
          iconOnly
          icon={<ChevronLeft />}
          aria-label="Page précédente"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        />
        {pageWindow(page, last).map((item, index) =>
          item === 'gap' ? (
            <span key={`gap-${index}`} aria-hidden="true" style={{ padding: '0 var(--space-1)' }}>
              …
            </span>
          ) : (
            <Button
              key={item}
              size="sm"
              variant={item === page ? 'primary' : 'ghost'}
              aria-current={item === page ? 'page' : undefined}
              aria-label={`Page ${item}`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          )
        )}
        <Button
          size="sm"
          variant="ghost"
          iconOnly
          icon={<ChevronRight />}
          aria-label="Page suivante"
          disabled={page >= last}
          onClick={() => onPageChange(page + 1)}
        />
      </div>
    </nav>
  )
}
